"use client";

import { useLanguage } from "@/components/LanguageProvider";
import Image from "next/image";
import Link from "next/link";
import { safeHref } from "@/lib/safe-href";
import { useMemo } from "react";

function hexToRgba(hex: string, opacity: number): string {
  if (!hex) return "";
  const h = hex.replace("#", "").padEnd(6, "0");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${opacity / 100})`;
}

type LinkItem = { href: string; label: string; labelAr?: string; target?: string };

type Props = {
  brandName: string;
  tagline: string;
  taglineAr?: string;
  logoSrc: string | null;
  brandTextColor: string;
  quickLinks: LinkItem[];
  legalLinks: LinkItem[];
  contactEmail?: string;
  contactPhone?: string;
  contactPhoneAr?: string;
  contactAddress?: string;
  contactAddressAr?: string;
  socialX?: string;
  socialLinkedIn?: string;
  footerBg: string;
  footerOpacity: number;
  footerTextColor: string;
  year: number;
};

export function SiteFooterClient({
  brandName,
  tagline,
  taglineAr,
  logoSrc,
  brandTextColor,
  quickLinks,
  legalLinks,
  contactEmail,
  contactPhone,
  contactPhoneAr,
  contactAddress,
  contactAddressAr,
  socialX,
  socialLinkedIn,
  footerBg,
  footerOpacity,
  footerTextColor,
  year,
}: Props) {
  const { t, dir, lang } = useLanguage();

  const bgStyle = useMemo(() => {
    if (footerBg) {
      return { backgroundColor: hexToRgba(footerBg, footerOpacity) };
    }
    return {
      backgroundColor: `color-mix(in srgb, var(--footer-background) ${footerOpacity}%, transparent)`,
    };
  }, [footerBg, footerOpacity]);

  const textStyle = useMemo(
    () => (footerTextColor ? { color: footerTextColor } : {}),
    [footerTextColor]
  );

  return (
    <footer
      style={bgStyle}
      className="relative mt-auto overflow-hidden border-t border-white/10 text-[var(--footer-foreground)]"
      dir={dir}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/5 via-transparent to-black/30"
      />
      <div
        style={textStyle}
        className="relative mx-auto grid max-w-6xl gap-10 px-4 py-14 md:grid-cols-2 md:px-6 lg:grid-cols-4"
      >
        {/* Brand */}
          <div>
            {logoSrc ? (
              <Image
                src={logoSrc}
                alt={brandName}
                width={120}
                height={40}
                className="mb-3 h-10 w-auto"
              />
            ) : null}
            <h2
              className={`mb-3 text-sm font-semibold uppercase tracking-wider${brandTextColor ? "" : " text-white"}`}
              style={brandTextColor ? { color: brandTextColor } : undefined}
            >
              {brandName}
            </h2>
            <p className="text-sm leading-relaxed">{lang === "ar" && taglineAr ? taglineAr : tagline}</p>
          </div>

        {/* Quick Links */}
        <div>
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-white">
            {t("footer.quickLinks")}
          </h2>
          <ul className="space-y-2 text-sm">
            {quickLinks.map((item) => (
              <li key={item.href}>
                <Link
                  href={safeHref(item.href)}
                  target={item.target !== "_self" ? item.target : undefined}
                  rel={item.target === "_blank" ? "noopener noreferrer" : undefined}
                  className="hover:text-[var(--accent)]"
                >
                  {lang === "ar" && item.labelAr ? item.labelAr : item.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Legal */}
        {legalLinks.length > 0 ? (
          <div>
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-white">
              {t("footer.legal")}
            </h2>
            <ul className="space-y-2 text-sm">
              {legalLinks.map((item) => (
                <li key={item.href}>
                  <Link
                    href={item.href}
                    target={item.target !== "_self" ? item.target : undefined}
                    rel={item.target === "_blank" ? "noopener noreferrer" : undefined}
                    className="hover:text-[var(--accent)]"
                  >
                    {lang === "ar" && item.labelAr ? item.labelAr : item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {/* Contact */}
        <div>
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-white">
            {t("footer.contact")}
          </h2>
          {contactEmail ? (
            <p className="text-sm">
              <a
                href={`mailto:${contactEmail}`}
                className="text-[var(--accent)] hover:underline"
              >
                {contactEmail}
              </a>
            </p>
          ) : null}
          {contactPhone || contactPhoneAr ? (
            <p className="mt-1 text-sm">
              <a
                href={`tel:${(contactPhone || "").replace(/\s+/g, "")}`}
                className="text-[var(--accent)] hover:underline"
              >
                {lang === "ar" && contactPhoneAr ? contactPhoneAr : contactPhone}
              </a>
            </p>
          ) : null}
          {contactAddress || contactAddressAr ? (
            <p className="mt-3 whitespace-pre-line text-xs leading-relaxed opacity-90">
              {lang === "ar" && contactAddressAr ? contactAddressAr : contactAddress}
            </p>
          ) : null}
          {(socialX || socialLinkedIn) ? (
            <div className="mt-4 flex gap-4">
              {socialX ? (
                <a
                  href={socialX}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-slate-300 hover:text-[var(--accent)]"
                  aria-label="X"
                >
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 22.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.008 5.961h-1.91z" />
                  </svg>
                </a>
              ) : null}
              {socialLinkedIn ? (
                <a
                  href={socialLinkedIn}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-slate-300 hover:text-[var(--accent)]"
                  aria-label="LinkedIn"
                >
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5v-9h3zM6.5 8.25A1.75 1.75 0 118.3 6.5a1.78 1.78 0 01-1.8 1.75zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.74 1.74 0 0013 14.19a.66.66 0 000 .14V19h-3v-9h2.9v1.3a3.11 3.11 0 012.7-1.4c1.55 0 3.36.86 3.36 3.66z" />
                  </svg>
                </a>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      <div className="relative border-t border-white/10 py-6 text-center text-xs text-slate-400">
        © {year} {brandName}. {t("footer.rights")}
      </div>
    </footer>
  );
}
